import React, { useContext, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import {
  CompanyModulesEnum,
  GetLinkedCompanyOutDTO,
  PageResponseGetLinkedCompanyOutDTO,
  PermissionTypesEnum,
} from '@api/ringil-api.v2';
import { convertToStringArray, isNullOrUndefined } from '@utils/utils';
import styled from 'styled-components';
import { InputText } from 'primereact/inputtext';
import { MultiSelect } from 'primereact/multiselect';
import { Dropdown } from '@components/RingilComponents/SafeDropdown';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { ApiContext } from '@api/api';
import { useTranslation } from 'react-i18next';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import { FilterPage, FilterSort, useFilter } from '@hooks/useFilter/Filter.tsx';
import clsx from 'clsx';
import { FaTrash } from 'react-icons/fa';
import { ArrowSvg } from '@icons/ArrowSvg';
import { useMediaQuery } from 'react-responsive';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import SvgDownloadIcon from '@app/pages/attachment/DownloadIcon.tsx';
import CloseSvg from '@icons/CloseSvg.tsx';
import { exportExcel } from '@utils/exports.ts';
import ConfirmRejectPartnerDialog from '@app/pages/settings/ConfirmRejectPartnerDialog.tsx';
import { Link } from 'react-router-dom';
import useNavigation from '@hooks/useNavigation.tsx';

interface ComponentProps {
}

export interface AllPartnersFilter {
  companyName?: string;
  companyModules?: CompanyModulesEnum[];
}

interface ActionPanelComponentProps {
  selectedObjects: GetLinkedCompanyOutDTO[];
  onSelectAllChange: (checked: boolean) => void;
  selectAllChecked: boolean;
  onApprove: () => void;
  onDelete: () => void;
  onSaveCSV: () => void;
  isMobile: boolean;
  hasActionPermission: boolean;
}

const AllPartners: React.FC<ComponentProps> = () => {
  const { t } = useTranslation('common');
  const { tr } = useTranslationLgs();
  const navigation = useNavigation();

  const [pageData, setPageData] = useState<PageResponseGetLinkedCompanyOutDTO>();
  const {
    getLinkedCompanies,
    getLinkedCompaniesExport,
    rejectModules,
    acceptAllModules,
    acceptModuleMultiple,
  } = useContext(ApiContext);
  const { hasPermission } = useContext(AuthenticatedUserContext);

  const hasActionPermission = hasPermission([PermissionTypesEnum.PERMISSION_SETTINGS_PARTNERS_RW]);

  const [confirmDialogVisible, setConfirmDialogVisible] = useState(false);

  const [hasMadeSortSelection, setHasMadeSortSelection] = useState(false);
  const [selectAllChecked, setSelectAllChecked] = useState(false);
  const isMobile = useMediaQuery({ maxWidth: 1024 });

  const [selectedObjects, setSelectedObjects] = useState<GetLinkedCompanyOutDTO[]>([]);
  const [companyIdToReject, setCompanyIdToReject] = useState<string | null>(null);

  const toggleCheckbox = (object: GetLinkedCompanyOutDTO) => {
    const found = selectedObjects.some(t => t.companyId === object.companyId);
    if (found) setSelectedObjects(selectedObjects.filter(t => t.companyId !== object.companyId));
    else setSelectedObjects([...selectedObjects, object]);
  };

  const isSelected = (object: GetLinkedCompanyOutDTO) => {
    return selectedObjects.some(t => t.companyId === object.companyId);
  };

  const {
    applyFilterValue,
    filter,
    applyPaging,
    page,
    onPage,
    applySorting,
    sort,
  } = useFilter<AllPartnersFilter>({
      onLoad: (page: FilterPage, filter: AllPartnersFilter) => {
        loadData(page.pageSize, page.pageNumber, filter, sort);
      },
      filterName: 'partner_list',
      // Tables built on RingilDataTable derive this from their filtersConf; this one builds its filter bar
      // by hand, so the keys are named here.
      filterKeys: ['companyName', 'companyModules'],
      arrayFields: ['companyModules'],
      defaultSort: { sortField: 'companyName', sortDirection: 'DESC' },
    },
  );

  const [loading, setLoading] = useState(false);
  const loadData = (pageSize: number, pageNumber: number, filter: AllPartnersFilter, sort: FilterSort) => {
    setLoading(true);
    getLinkedCompanies(
      {
        ...filter,
      },
      {
        pageSize,
        pageNumber: pageNumber,
        sortField: sort.sortField,
        sortDirection: sort.sortDirection,
      },
      data => {
        setLoading(false);
        setPageData(data);
      },
    );
  };

  const handleApproveModules = (companyId: string) => {
    acceptAllModules(companyId, () => {
      loadData(page.pageSize, page.pageNumber, filter, sort);
    });
  };

  const handleRejectModules = (companyId: string) => {
    setCompanyIdToReject(companyId);
    setConfirmDialogVisible(true);
  };

  const handleConfirmDelete = () => {
    rejectModules(companyIdToReject, () => {
      setConfirmDialogVisible(false);
      loadData(page.pageSize, page.pageNumber, filter, sort);
    });
  };

  const handleCancelDelete = () => {
    setCompanyIdToReject(null);
    setConfirmDialogVisible(false);
  };

  const handleApproveAllCompanies = () => {
    const selectedCompanyIds = selectedObjects.map(company => company.companyId);

    acceptModuleMultiple({
      inheritedCompanyIds: selectedCompanyIds
    }, () => {
      loadData(page.pageSize, page.pageNumber, filter, sort);
      setSelectedObjects([]);
      setSelectAllChecked(false);
    });
  };

  const renderCheckboxColumn = (row: GetLinkedCompanyOutDTO) => {
    return (
      <div className="flex gap-2 items-center min-h-10">
        <Checkbox
          onChange={e => {
            toggleCheckbox(row);
          }}
          checked={isSelected(row)}
        />
      </div>
    );
  };

  const renderCompanyNameColumn = (row: GetLinkedCompanyOutDTO) => {
    return (
      <div className="flex items-center gap-2 min-h-10">
        <Link
          to={navigation.createNavigationLink(navigation.urlFunctions.partnerDetailUrlInfo(row.companyId))}
        >
            <span
              className={'text-[16px] font-normal leading-normal tracking-[0.24px] text-content-brand font-dm-sans'}
            >
              {row.companyName}
            </span>
        </Link>
      </div>
    );
  };

  const renderCompanyModulesColumn = (row: GetLinkedCompanyOutDTO) => {
    return (
      <div className="flex gap-2 items-center min-h-10">
        {row.activeCompanyModule?.join(', ')}
      </div>
    );
  };

  const renderCompanyRequestedModulesColumn = (row: GetLinkedCompanyOutDTO) => {
    return (
      <div className="flex gap-2 items-center min-h-10">
        {row.askedCompanyModule?.join(', ')}
      </div>
    );
  };

  const renderActions = (row: GetLinkedCompanyOutDTO) => {
    const hasModulesForApproval = row.askedCompanyModule && row.askedCompanyModule.length > 0;

    if (!hasModulesForApproval || !hasActionPermission) {
      return <div></div>;
    }

    return (
      <div className="flex gap-2 items-center justify-end">
        <RingilButton
          mode={'table'}
          label={tr('AllPartners.approveButton', 'Approve')}
          onClick={() => handleApproveModules(row.companyId)}
        />
        <RingilButton
          mode={'table'}
          onClick={() => handleRejectModules(row.companyId)}
          icon={<CloseSvg />}
        />
      </div>
    );
  };

  if (isNullOrUndefined(filter)) return <>Loading....</>;

  const handleSelectAllChange = (checked: boolean) => {
    setSelectAllChecked(checked);
    if (checked) {
      if (pageData?.data) {
        setSelectedObjects(pageData.data);
      }
    } else {
      setSelectedObjects([]);
    }
  };

  const handleClearSelected = () => {
    setSelectAllChecked(false);
    setSelectedObjects([]);
  };

  const fetchAllPartners = (callback: (allRows: GetLinkedCompanyOutDTO[]) => void) => {
    getLinkedCompaniesExport(
      {
        ...filter,
      },
      d => {
        callback(d);
      },
    );
  };

  const handleSaveToCSV = () => {
    fetchAllPartners(allRows => {
      const exportData = allRows.map(partner => ({
        companyId: partner.companyId,
        companyName: partner.companyName,
        activeModules: partner.activeCompanyModule?.join(', '),
        requestedModules: partner.askedCompanyModule?.join(', '),
        userId: partner.user.id,
        userEmail: partner.user.email,
        userName: partner.user.fullName,
        userPhone: partner.user.phone,
      }));
      exportExcel(exportData, 'partners');
    });
  };

  const renderMobileCard = (row: GetLinkedCompanyOutDTO) => {
    const hasModulesForApproval = row.askedCompanyModule && row.askedCompanyModule.length > 0;

    return (
      <div key={row.companyId}
           className="bg-white p-8 pb-6 shadow-[0_1px_0_0_rgba(229,231,235,1)] -mx-4 relative z-10 mb-1">
        <div className="flex items-center gap-3">
          <Checkbox onChange={() => toggleCheckbox(row)} checked={isSelected(row)}
                    className="mt-1" />
          <div className="mt-3">{row.companyName}</div>
        </div>
        {row.activeCompanyModule && row.activeCompanyModule.length > 0 && (
          <div className="mt-3">{row.activeCompanyModule.join(', ')}</div>
        )}
        {hasModulesForApproval && (
          <>
            <div className="mt-3">{row.askedCompanyModule.join(', ')}</div>
            <div className={`flex flex-row gap-3 mt-3`}>
              <RingilButton
                mode={'table'}
                label={tr('AllPartners.approveButton', 'Approve')}
                onClick={() => handleApproveModules(row.companyId)}
              />
              <RingilButton
                mode={'table'}
                onClick={() => handleRejectModules(row.companyId)}
                icon={<CloseSvg />}
              />
            </div>
          </>
        )}
      </div>
    )
      ;
  };

  return (
    <>
      <div className={clsx('r-legacy-list-fields', 'font-[\'DM Sans\']', isMobile && '-mx-4 px-8 pt-8')}>
        <div className="flex justify-between items-center mb-6">
          <Heading className="!m-0">{t(`AllPartners.title`, `Connected companies`)}</Heading>
        </div>

        <div className="filters">
          <FilterRow>
            <div className="primary-filters">
              <div className="field" key="companyName">
                <InputText
                  value={filter.companyName || ''}
                  onChange={e => {
                    const { value } = e.target as HTMLInputElement;
                    applyFilterValue('companyName', value);
                  }}
                  placeholder={tr(`AllPartners.filterLabelCompanyName`, `Company name`)}
                  className="w-full"
                />
              </div>
              <div className="field" key="companyModule">
                <MultiSelect
                  value={convertToStringArray(filter.companyModules)}
                  options={Object.values(CompanyModulesEnum)
                    .filter(v => typeof v === 'string')
                    .map(val => ({
                      name: tr(`CompanyModuleEnum.${val}`, val),
                      value: val,
                    }))}
                  onChange={e => {
                    applyFilterValue('companyModules', e.value as CompanyModulesEnum[]);
                  }}
                  showClear={true}
                  optionLabel="name"
                  optionValue="value"
                  itemTemplate={(option: { name: string; value: CompanyModulesEnum }) => <>{option.name}</>}
                  placeholder={tr(`AllPartners.filterLabelCompanyModules`, `Modules`)}
                  className="w-full"
                />
              </div>
            </div>
          </FilterRow>
        </div>

        {isMobile && (
          <div className="mt-4 -mx-4 bg-[#E7EBEE]">
            <div className="px-4 py-4">
              <Dropdown
                value={hasMadeSortSelection && sort.sortField === 'companyName' ? `${sort.sortField}_${sort.sortDirection}` : null}
                options={[
                  {
                    label: tr('AllPartners.sortLabelCompanyNameAsc', 'Company name (A-Z)'),
                    value: 'companyName_ASC',
                  },
                  {
                    label: tr('AllPartners.sortLabelCompanyNameDesc', 'Company name (Z-A)'),
                    value: 'companyName_DESC',
                  },
                  {
                    label: tr('AllPartners.sortLabelRequestedModulesAsc', 'Requested modules (Z-A)'),
                    value: 'companyName_DESC',
                  },
                  {
                    label: tr('AllPartners.sortLabelRequestedModulesDesc', 'Requested modules (Z-A)'),
                    value: 'companyName_DESC',
                  },
                ]}
                onChange={e => {
                  const [field, direction] = e.value.split('_');
                  setHasMadeSortSelection(true);
                  applySorting(field, direction as 'ASC' | 'DESC');
                }}
                placeholder={tr('AllPartners.sortLabelSortBy', 'Sort by Company name')}
                className="w-full"
              />
            </div>
          </div>
        )}
      </div>
      {pageData && (
        <div className="datatable-filter p-datatable-customers">
          <ActionPanel
            selectedObjects={selectedObjects}
            onSelectAllChange={handleSelectAllChange}
            selectAllChecked={selectAllChecked}
            onApprove={handleApproveAllCompanies}
            onDelete={handleClearSelected}
            onSaveCSV={handleSaveToCSV}
            isMobile={isMobile}
            hasActionPermission={hasActionPermission}
          />


          {isMobile ? (
            <div>
              {pageData.data && pageData.data.length > 0 ? (
                pageData.data.map(expedition => renderMobileCard(expedition))
              ) : (
                <div className="p-4 text-center">{t('global.noRecordFound')}</div>
              )}
              {pageData.total > page.pageSize && (
                <div className="mt-4">
                  <div className="flex justify-between items-center px-4">
                    <div className="text-sm text-gray-600">
                      {tr('pagination.showing', 'Showing')} {(page.pageNumber - 1) * page.pageSize + 1} - {Math.min(page.pageNumber * page.pageSize, pageData.total)}{' '}
                      {tr('pagination.of', 'of')} {pageData.total}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        icon="pi pi-chevron-left"
                        onClick={() => applyPaging({ pageNumber: page.pageNumber - 1, pageSize: page.pageSize })}
                        disabled={page.pageNumber === 1}
                        className="p-button-text !text-orange-500 hover:!text-orange-600"
                      />
                      <Button
                        icon="pi pi-chevron-right"
                        onClick={() => applyPaging({ pageNumber: page.pageNumber + 1, pageSize: page.pageSize })}
                        disabled={page.pageNumber * page.pageSize >= pageData.total}
                        className="p-button-text !text-orange-500 hover:!text-orange-600"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <DataTable
              emptyMessage={t('global.noRecordFound')}
              value={pageData.data}
              rows={pageData.size}
              className="p-datatable-customers"
              paginator
              lazy
              totalRecords={pageData.total}
              onPage={onPage}
              loading={loading}
              first={(pageData.page - 1) * pageData.size}
              sortMode="single"
              removableSort={false}
              onSort={e => {
                applySorting(e.sortField, e.sortOrder === 1 ? 'ASC' : 'DESC');
              }}
              sortField={sort.sortField}
              sortOrder={sort.sortDirection === 'ASC' ? 1 : -1}
              rowsPerPageOptions={[10, 25, 50, 100]}
            >
              <Column
                header={''}
                body={row => renderCheckboxColumn(row)}
                style={{ width: 30 }}
              ></Column>
              <Column
                sortable
                sortField={'companyName'}
                header={tr(`AllPartners.dataTableCompanyNameHeaderTitle`, `Company name`)}
                body={row => renderCompanyNameColumn(row)}
              ></Column>
              <Column
                sortable
                sortField={'companyModules'}
                header={tr(`AllPartners.dataTableModulesHeaderTitle`, `Owned modules`)}
                body={row => renderCompanyModulesColumn(row)}></Column>
              <Column
                sortable
                sortField={'companyModulesRequest'}
                header={tr('AllPartners.dataTableModulesRequestedHeaderTitle', 'Modules for approval')}
                body={data => renderCompanyRequestedModulesColumn(data)} />
              <Column body={row => renderActions(row)}></Column>
            </DataTable>
          )}
        </div>
      )}

      <ConfirmRejectPartnerDialog
        visible={confirmDialogVisible}
        onHide={handleCancelDelete}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
};

export default AllPartners;

const ActionPanel: React.FC<ActionPanelComponentProps> = ({
                                                            selectedObjects,
                                                            onSelectAllChange,
                                                            selectAllChecked,
                                                            onApprove,
                                                            onSaveCSV,
                                                            onDelete,
                                                            isMobile,
                                                            hasActionPermission = false,
                                                          }) => {
  const { tr } = useTranslationLgs();
  const [showExtraMobileActions, setShowExtraMobileActions] = useState(false);
  const selectedCount = selectedObjects.length;

  return (
    <>
      <div
        style={{
          background: '#E7EBEE',
          padding: isMobile ? '12px 16px' : '16px 32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontFamily: 'DM Sans, sans-serif',
          marginBottom: 1,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Checkbox checked={selectAllChecked} onChange={e => onSelectAllChange(e.checked as boolean)}
                    style={{ marginRight: 8 }} />
          <span style={{ whiteSpace: 'nowrap' }}>
            {selectedCount} {tr('AllPartners.actionMenuSelectedLabel', 'selected')}
          </span>
          <ArrowIcon>
            <ArrowSvg />
          </ArrowIcon>

          {hasActionPermission && (
            <ActionButton onClick={onApprove} disabled={selectedCount === 0 || !selectedObjects.some(obj => obj.askedCompanyModule?.length > 0)}>
              {tr('AllPartners.actionMenuApproveLabel', 'Approve')}
            </ActionButton>
          )}

          {isMobile ? (
            <Button
              icon="pi pi-ellipsis-h"
              onClick={() => setShowExtraMobileActions(prev => !prev)}
              className="p-button-text p-button-rounded p-button-plain"
              style={{ marginLeft: '8px', color: '#FF6B00' }}
            />
          ) : (
            <>
              <DeleteButton onClick={onDelete} />
            </>
          )}
        </div>
        {!isMobile && (
          <ExportButton onClick={onSaveCSV}>
            <SvgDownloadIcon style={{ marginRight: 8 }} />
            {tr('AllPartners.actionMenuSaveAsXLSXLabel', 'Save as XLSX')}
          </ExportButton>
        )}
      </div>

      {isMobile && showExtraMobileActions && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            background: '#F7F8F9',
            borderBottom: '1px solid #E7EBEE',
            paddingTop: '4px',
            paddingBottom: '4px',
          }}
        >
          <ActionButton
            onClick={onDelete}
            disabled={selectedCount === 0}
            style={{ justifyContent: 'flex-start', padding: '10px 16px', width: '100%', color: '#fe5000', margin: 0 }}
          >
            {tr('AllPartners.actionMenuDeleteLabel', 'Delete')}
          </ActionButton>
          <ActionButton
            onClick={onDelete}
            disabled={selectedCount === 0}
            style={{ justifyContent: 'flex-start', padding: '10px 16px', width: '100%', color: '#fe5000', margin: 0 }}
          >
            {tr('AllPartners.actionMenuSaveAsXLSXLabel', 'Save as XLSX')}
          </ActionButton>
        </div>
      )}
    </>
  );
};

const FilterRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin: 1rem 0;
  font-family: 'DM Sans', sans-serif;

  .primary-filters,
  .additional-filters {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 0.5rem 1rem;
    width: 100%;

    @media (max-width: 1280px) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    @media (max-width: 768px) {
      grid-template-columns: repeat(1, minmax(0, 1fr));
    }

    &.hidden {
      display: none;
    }

    .field {
      min-width: 200px;

      @media (max-width: 768px) {
        width: 100%;
        min-width: 100%;
      }

      .p-inputtext,
      .p-calendar,
      .p-multiselect,
      .p-dropdown {
        width: 100%;
      }
    }

    .field-checkbox {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    .dates-group {
      display: flex;
      gap: 0.5rem;
      grid-column: span 1;

      @media (max-width: 1280px) {
        grid-column: span 1;
      }

      @media (max-width: 768px) {
        grid-column: span 1;
        flex-direction: row;
      }

      .field {
        min-width: unset;
        flex: 1;
      }
    }
  }
`;


const ActionButton = styled.button<{ disabled?: boolean }>`
  margin: 0 8px;
  color: #ff6b00;
  background: none;
  border: none;
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  opacity: ${props => (props.disabled ? 0.5 : 1)};
  font-family: 'DM Sans', sans-serif;
  font-size: 1rem;
  padding: 0;
  transition: opacity 0.2s ease-in-out;
  white-space: nowrap;

  &:hover {
    opacity: ${props => (props.disabled ? 0.5 : 0.8)};
  }
`;

const DeleteButton = styled(FaTrash)`
  color: #fe5000;
  margin-left: 16px;
  cursor: pointer;
  transition: opacity 0.2s ease-in-out;

  &:hover {
    opacity: 0.8;
  }
`;

const ExportButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-right: 16px;
  background: none;
  border: none;
  font-weight: 400;
  cursor: pointer;
  font-family: inherit;
  font-size: 1rem;
  color: #fe5000;
  padding: 0;
  transition: opacity 0.2s ease-in-out;

  &:hover {
    opacity: 0.8;
  }
`;

const ArrowIcon = styled.span`
  display: inline-flex;
  align-items: center;
  margin: 0 8px 0 8px;
  color: #b0b0b0;
  font-size: 18px;

  svg {
    display: block;
  }
`;

const Heading = styled.h1`
  padding: 0;
  font-size: 26px;
  font-family: 'DM Sans', sans-serif;
  font-weight: 400;
`;
